import { useEffect, useState } from 'react';
import Button from '../common/Button';
import { useNavigate } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import { useGlobalSettings } from '../../hooks/useGlobalSettings';
import Animation from '../common/Animation';
import { useAvatar } from '../../hooks/useAvatar';
import { useStartpageData } from '../../hooks/pages/useStartpageData';

const LAYOUTS = {
  // ==== 16:9 Landscape, ~1920×1080 ====
  FHD: {
    logo:   { top: 48,  left: 48,  height: 64 },
    avatar: { top: '53%', left: '8%', width: 500, height: 1000, scale:'180%' /* translateY: '-50%' */ },
    box:    { top: '40%', left: '50%', width: '90%', paddingY: 80,  paddingX: 48,  radius: 24 },
    question: { fontSizeClass: 'text-5xl', mb: 64 },
    buttons:  {
      cols: 2, gapX: 14, gapY: 44,
      ui: { px: 24, py: 20, minH: 150, radius: 12, labelSize: 36, descSize: 24, gap: 12 }
    },
  },

  // ==== 16:9 Landscape, ~2560×1440 (WQHD) ====
  QHD: {
    logo:   { top: 72,  left: 72,  height: 96 },
    avatar: { top: '67%', left: '3%', width: 500, height: 1000, scale: '280%' },
    box:    { top: '40%', left: '50%', width: '85%', paddingY: 120, paddingX: 72, radius: 28 },
    question: { fontSizeClass: 'text-6xl', mb: 128 },
    buttons:  {
      cols: 2, gapX: 24, gapY: 32,
      ui: { px: 28, py: 22, minH: 180, radius: 14, labelSize: 64, descSize: 32, gap: 14 }
    },
  },

  // ==== 16:9 Landscape, ~3840×2160 ====
  UHD: {
    logo:   { top: 96,  left: 96,  height: 128 },
    avatar: { top: '80%', left: '5%', width: 500, height: 1000, scale: '380%' },
    box:    { top: '40%', left: '50%', width: '90%', paddingY: 156, paddingX: 128, radius: 32 },
    question: { fontSizeClass: 'text-8xl', mb: 128 },
    buttons:  {
      cols: 2, gapX: 60, gapY: 80,
      ui: { px: 64, py: 96, minH: 220, radius: 25, labelSize: 105, descSize: 60, gap: 16 }
    },
  },
};

function detectProfileSmart() {
  if (typeof window === 'undefined') return 'FHD';
  const vw = window.innerWidth, vh = window.innerHeight;
  const dpr = window.devicePixelRatio || 1;

  const effLong = Math.max(vw, vh) * dpr;   
  const effShort = Math.min(vw, vh) * dpr;  

  const forced = new URLSearchParams(window.location.search).get('profile');
  if (forced && LAYOUTS[forced]) return forced;

  // Landscape 16:9
  if ((effLong >= 4000 && effShort >= 2100) || (effLong >= 3800 && effShort >= 2100)) return 'UHD'; 
  if (effLong >= 2500 && effShort >= 1400) return 'QHD';
  return 'FHD';
}

export default function StartPageFlipped({
  station,
  theme,
  rotations = {},
  origins = {},
}) {
  const navigate = useNavigate();
  const { data: pageData, error: pageDataError } = useStartpageData(station);
  const { globalSettings, error: globalSettingsError } = useGlobalSettings();
  const { avatarData, error: avatarDataError } = useAvatar('Startseite');

  const [profile, setProfile] = useState(detectProfileSmart);
  useEffect(() => {
    const onResize = () => setProfile(detectProfileSmart());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    onResize();
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);

  if (globalSettingsError || pageDataError || avatarDataError) {
    return <div>Fehler: {globalSettingsError || pageDataError || avatarDataError}</div>;
  }
  if (!globalSettings || !pageData || !avatarData) {
    return <div>Laden...</div>;
  }
  if (!pageData.buttons) {
    return <div>Fehler: Buttons konnten nicht geladen werden.</div>;
  }

  const { question, buttons } = pageData;
  const { logoUrl } = globalSettings;
  const avatar = avatarData[0]?.animation;

  const toDeg = (v) => (typeof v === 'number' ? v : parseFloat(v || 0));
  const ori   = (k, fallback='center') => origins?.[k] || fallback;
  const rootAngle = ((toDeg(rotations.root) % 360) + 360) % 360;

  const rootBase = {
    backgroundColor: theme.backgroundColor,
    color: theme.textColor,
    overflow: 'hidden',
    position: 'fixed',
    inset: 0,
  };

  let rootStyle = { ...rootBase };
  if (rootAngle === 90) {
    rootStyle = { ...rootBase, width: '100vh', height: '100vw', transformOrigin: origins?.root || 'top left', transform: 'rotate(90deg) translateY(-100%)' };
  } else if (rootAngle === 270) {
    rootStyle = { ...rootBase, width: '100vh', height: '100vw', transformOrigin: origins?.root || 'top left', transform: 'rotate(270deg) translateX(-100%)' };
  } else if (rootAngle === 180) {
    rootStyle = { ...rootBase, transformOrigin: ori('root'), transform: 'rotate(180deg)' };
  } else {
    rootStyle = { ...rootBase, transformOrigin: ori('root'), transform: `rotate(${rootAngle}deg)` };
  }

  const cfg = LAYOUTS[profile];

  return (
    <div className="fixed inset-0 overflow-hidden" style={rootStyle}>

      <div className="relative w-full h-full">
        {/* Logo */}
        <img
          src={logoUrl}
          alt="Logo"
          style={{
            position: 'absolute',
            top: cfg.logo.top,
            left: cfg.logo.left,
            height: cfg.logo.height,
            rotate: typeof rotations.logo === 'number' ? `${rotations.logo}deg` : (rotations.logo || '0deg'),
            transformOrigin: ori('logo'),
          }}
        />

        {/* Avatar */}
        <div
          style={{
            position: 'absolute',
            top: cfg.avatar.top,
            left: cfg.avatar.left,
            width: cfg.avatar.width,
            height: cfg.avatar.height,
            ...(cfg.avatar.translateY ? { translate: `0 ${cfg.avatar.translateY}` } : {}),
            ...(cfg.avatar.scale ? { scale: cfg.avatar.scale } : {}),
            rotate: typeof rotations.avatar === 'number' ? `${rotations.avatar}deg` : (rotations.avatar || '0deg'),
            transformOrigin: ori('avatar', 'bottom left'),
          }}
        >
          <Animation src={avatar} className="w-full h-full object-contain" />
        </div>

        {/* Textbox */}
        <div
          style={{
            position: 'absolute',
            top: cfg.box.top,
            left: cfg.box.left,
            translate: '-50% -50%',
            width: cfg.box.width,
            padding: `${cfg.box.paddingY}px ${cfg.box.paddingX}px`,
            margin: 0,
            backgroundColor: theme.foregroundColor,
            borderRadius: cfg.box.radius,
            rotate: typeof rotations.box === 'number' ? `${rotations.box}deg` : (rotations.box || '0deg'),
            transformOrigin: ori('box'),
          }}
        >
          {/* Frage */}
          <div className="text-center" style={{ marginBottom: cfg.question.mb }}>
            <ReactMarkdown
              className={`${cfg.question.fontSizeClass} uppercase`}
              components={{ p: (props) => <p style={{ margin: 0 }} {...props} /> }}
              style={{
                rotate: typeof rotations.text === 'number' ? `${rotations.text}deg` : (rotations.text || '0deg'),
                transformOrigin: ori('text'),
                lineHeight: cfg.question.lineHeight,
              }}
            >
              {question}
            </ReactMarkdown>
          </div>

          {/* Buttons */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: `repeat(${cfg.buttons.cols}, 1fr)`,
              columnGap: cfg.buttons.gapX,
              rowGap: cfg.buttons.gapY,
            }}
          >
            {buttons.map((button, idx) => (
              <Button
                key={idx}
                buttonnumber={button.number}
                text={button.text}
                theme={theme}
                ui={cfg.buttons.ui}  
                handleClick={() => {
                  if (button.route) navigate(button.route);
                  else console.warn(`Button ${idx} hat keine gültige Route`);
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}